import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Settings as SettingsIcon, Sparkles, CheckCircle2, AlertCircle, ExternalLink,
  Loader2, DollarSign, Check,
} from 'lucide-react';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthStore } from '@/store/auth';
import { useEvieStore } from '@/store/evie';
import {
  fetchTenantConnectStatus,
  startTenantConnectOnboarding,
  fetchTenantDashboardLink,
  fetchTenantSettings,
  updateTenantSettings,
  getDisplayErrorMessage,
} from '@/lib/api';
import { PLATFORM_HELD_PAYOUTS_COPY, tenantPayoutsView } from '@/lib/tenantPayoutsView';
import { TenantBilling } from '@/pages/tenants/TenantBilling';
import { InstallAppButton } from '@/components/InstallAppButton';
import styles from './SettingsPage.module.css';
import { IdleModelSection } from './IdleModelSection';
import { DriverPriceSection } from './DriverPriceSection';
import { FreightSettingsSection } from './FreightSettingsSection';
import { ServiceRequestSettingsSection } from './ServiceRequestSettingsSection';
import { RemediationSettingsSection } from './RemediationSettingsSection';
import { StandingAuthoritySection } from './StandingAuthoritySection';
import { ApproversSection } from './ApproversSection';
import { RegressionWatchSettingsSection } from './RegressionWatchSettingsSection';
import { LeadInternalAddressesSection } from './LeadInternalAddressesSection';
import { UtilityRatesSection } from './UtilityRatesSection';
import { MarketPricesSection } from './MarketPricesSection';
import { PoAutoReleaseSection } from './PoAutoReleaseSection';
import { InboundMailSection } from './InboundMailSection';

/**
 * Tenant-wide preferences page.
 *
 * Active sections:
 *   - Payouts (Stripe Connect Express onboarding for the tenant's
 *     building) — operator-side mirror of the resident flow on the
 *     PWA. Replaces the prior manual Stripe-Dashboard + DB-write
 *     path that was the only way to onboard a tenant pre-this-commit.
 *
 * Reserved for future:
 *   - Tenant-wide preferences with no better home: timezone, branding,
 *     default currency, future notification cadence, etc.
 *
 * Domain-specific blocks that moved away from this page:
 *   - Reward Points (gamification rates, multipliers, redemption
 *     thresholds) → /earning-rules under the GAMIFICATION nav section.
 *   - Resident-Managed Chargers toggle → top of /locations.
 */
export function SettingsPage() {
  useRecentRoute('Settings');
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenant_id;
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><SettingsIcon size={18} /></div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Settings</h1>
          <p className={styles.subtitle}>Tenant-wide preferences.</p>
        </div>
      </div>

      {/* Payouts panel — only renders when we have a tenant_id in
          the auth store. The login flow always populates it, so the
          absent-tenant case is the edge state (unauthenticated route
          slipped through, shouldn't happen in practice). */}
      {tenantId && <PayoutsSection tenantId={tenantId} />}

      {/* Subscription billing — the pay-EVI direction (per-charger
          subscription). Scoped to the logged-in user's own tenant, so a
          tenant_admin manages their building's plan here and a super_admin
          sees their own tenant. Cross-tenant management lives on the
          super_admin /tenants surface. The route gates via
          requireTenantScope (own tenant OR super_admin). */}
      {tenantId && (
        <section className={styles.card}>
          <div className={styles.cardTitleRow}>
            <h2 className={styles.cardTitle}>Subscription billing</h2>
          </div>
          <p className={styles.cardLead}>
            Your building&rsquo;s per-charger subscription to EVI. Add a card, start the
            plan, and switch between monthly and annual billing here.
          </p>
          <TenantBilling tenantId={tenantId} />
        </section>
      )}

      {/* Platform Admin Fee — Phase 7c. super_admin-only; the
          backend PATCH also strips admin_fee_bps from non-super_admin
          bodies as defense-in-depth (mirror of Phase 7b's Charger
          agent_id pattern). */}
      {isSuperAdmin && tenantId && <PlatformAdminFeeSection tenantId={tenantId} />}
      {isSuperAdmin && tenantId && <IdleModelSection />}
      {/* Warren's minimum margin on the driver price (33% until set) and the review's markup for New jobs (ruling D3, 21 October 2026). */}
      {isSuperAdmin && <DriverPriceSection />}
      {/* Freight priced at checkout by API: the pickup, the buffer and the manual lanes (ruling B, 21 October 2026). */}
      {isSuperAdmin && <FreightSettingsSection />}
      {/* Minutes offline before a service call opens (ruling H, 21 October 2026). */}
      {isSuperAdmin && <ServiceRequestSettingsSection />}
      {/* Automatic fixes, the remediation ladder's kill switch, limits and log (Warren, 28 October 2026, evening). */}
      {isSuperAdmin && <RemediationSettingsSection />}
      {/* Standing authority: what the platform does with driver money on its own, within Warren's limits (29 October 2026). The Api
          decides who sees it (a live Super Admin) and who may loosen it (the owner); the section shows nothing on a refusal. */}
      <StandingAuthoritySection />
      <ApproversSection />
      {/* The regression watch: each site against its own pre-cutover baseline, daily (Warren, 29 October 2026, item 7). */}
      {isSuperAdmin && <RegressionWatchSettingsSection />}
      {/* The staff test list: leads from these addresses are internal (Warren, 28 October 2026). */}
      {isSuperAdmin && <LeadInternalAddressesSection />}
      {/* The support@ intake address, the capture checklist, received mail and Re-read (Warren, 29 October 2026). The Api
          decides who sees it (Super Admin): the section asks and shows nothing on a refusal, so no persona gates it here. */}
      <InboundMailSection />
      {/* EVI's utility rate table and Warren's PO auto-release limit (21 October 2026). The Api decides who sees and changes
          them (EVI staff; the limit, Super Admin): each section shows nothing on a refusal. */}
      {(isSuperAdmin || user?.role === 'operator') && <UtilityRatesSection />}
      {/* EVI's market price table, the same permission as the utility rates (Warren, 21 October 2026, evening). */}
      {(isSuperAdmin || user?.role === 'operator') && <MarketPricesSection />}
      {(isSuperAdmin || user?.role === 'operator') && <PoAutoReleaseSection />}

      {/* Per-operator EVII voice preference (always available, any role). */}
      <EvieVoiceSection />

      {/* Install EVI Admin — fires the Android/desktop install prompt, or shows
          the iOS Add-to-Home-Screen hint. Self-hides when already installed or
          when no install path is available. Mirrors the driver app's Settings. */}
      <InstallAppButton />

      <section className={styles.placeholder}>
        <Sparkles size={28} className={styles.placeholderIcon} />
        <h2 className={styles.placeholderTitle}>More settings coming soon</h2>
        <p className={styles.placeholderBody}>
          Reward Points moved to <strong>Earning Rules</strong> under
          Gamification. Resident-Managed Chargers moved to the top of
          the <strong>Locations</strong> page. Tenant-wide preferences
          (timezone, branding, defaults) will land here as they&rsquo;re
          built.
        </p>
      </section>
    </div>
  );
}

/**
 * EVII voice — per-operator female/male preference for the voice EVII uses when
 * reading answers aloud in voice mode. A personal (not tenant) preference,
 * persisted per user in the evie store and keyed by user id, so it sticks for
 * each operator and takes effect on the next spoken answer. The operator-facing
 * label is only "Female"/"Male"; the underlying Polly voice names never surface.
 */
function EvieVoiceSection() {
  const userId = useAuthStore((s) => s.user?._id ?? null);
  const voice = useEvieStore((s) => (userId ? s.voiceByUser[userId] ?? 'female' : 'female'));
  const setVoiceChoice = useEvieStore((s) => s.setVoiceChoice);
  // Mic auto-start on EVII open (per-operator, persisted; default on).
  const micAutostart = useEvieStore((s) => (userId ? s.micAutostartByUser[userId] ?? true : true));
  const setMicAutostart = useEvieStore((s) => s.setMicAutostart);

  return (
    <section className={styles.card}>
      <div className={styles.cardTitleRow}>
        <h2 className={styles.cardTitle}>EVII™ voice</h2>
      </div>
      <p className={styles.cardLead}>
        The voice EVII™ uses when reading answers aloud in voice mode. Saved to
        your account and applied to the next spoken answer.
      </p>
      <div className={styles.voiceToggle} role="radiogroup" aria-label="EVII™ voice">
        {(['female', 'male'] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={voice === opt}
            className={`${styles.voiceOption} ${voice === opt ? styles.voiceOptionActive : ''}`}
            onClick={() => setVoiceChoice(userId, opt)}
          >
            {opt === 'female' ? 'Female' : 'Male'}
          </button>
        ))}
      </div>
      {/* Mic auto-start on open (per-operator, persisted; default on). */}
      <p className={styles.cardLead}>
        Auto-start the mic when EVII™ opens, so you can speak right away (you’ll
        be asked for mic permission the first time). Off waits for a tap.
      </p>
      <div className={styles.voiceToggle} role="radiogroup" aria-label="Auto-start mic">
        {([true, false] as const).map((on) => (
          <button
            key={String(on)}
            type="button"
            role="radio"
            aria-checked={micAutostart === on}
            className={`${styles.voiceOption} ${micAutostart === on ? styles.voiceOptionActive : ''}`}
            onClick={() => setMicAutostart(userId, on)}
          >
            {on ? 'On' : 'Off'}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Payouts onboarding section. Five states:
 *   - loading: connect-status query in flight
 *   - not_started: no Connect account on the Tenant doc → "Start setup"
 *   - in_progress: account exists, charges_enabled === false → resume +
 *                  surface Stripe's requirements_currently_due
 *   - ready: charges_enabled === true → "Open Stripe dashboard"
 *   - platform_held: the Api's platform_held flag (ruling 2) wins over the
 *                    rest: one plain line, no button
 */
function PayoutsSection({ tenantId }: { tenantId: string }) {
  const statusQ = useQuery({
    queryKey: ['tenant-connect-status', tenantId],
    queryFn: () => fetchTenantConnectStatus(tenantId),
    // Stripe-status round-trip on every status query: fine here,
    // Settings is a low-frequency surface. Same staleTime the
    // resident ConnectCompletePage uses.
    staleTime: 30_000,
    // Re-check on focus so an operator who just finished onboarding
    // in another tab sees the updated status when they tab back.
    refetchOnWindowFocus: true,
  });
  const [busy, setBusy] = useState<'starting' | 'opening' | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleStart = async (): Promise<void> => {
    setErrorMsg('');
    setBusy('starting');
    try {
      const { url } = await startTenantConnectOnboarding(tenantId);
      window.location.href = url;
    } catch (err) {
      setBusy(null);
      setErrorMsg(
        err instanceof Error && err.message
          ? err.message
          : "Couldn't start payout setup. Try again in a moment.",
      );
    }
  };

  const handleOpenDashboard = (): void => {
    setErrorMsg('');
    // iOS Safari popup heuristic — sync window.open in the click
    // handler. NOTE: must NOT pass noopener/noreferrer here — those
    // flags force window.open to return null per the HTML spec, and
    // we need the Window reference so the .then below can set
    // popup.location.href once the fetch resolves. The destination
    // (Stripe-hosted dashboard) is trusted, and we null the opener
    // post-redirect to close the back-channel anyway. Same fix
    // applied to MyChargerEarningsPage.
    const popup = window.open('about:blank', '_blank');
    setBusy('opening');
    fetchTenantDashboardLink(tenantId)
      .then(({ url }) => {
        if (popup) {
          popup.location.href = url;
          // Break window.opener now that the redirect is fired —
          // we no longer need the back-reference and the popup
          // shouldn't be able to navigate this tab.
          popup.opener = null;
        }
      })
      .catch((err: unknown) => {
        if (popup) popup.close();
        setErrorMsg(
          err instanceof Error && err.message
            ? err.message
            : "Couldn't open Stripe just now. Try again in a moment.",
        );
      })
      .finally(() => setBusy(null));
  };

  return (
    <section className={styles.card}>
      <div className={styles.cardTitleRow}>
        <h2 className={styles.cardTitle}>Payouts</h2>
      </div>

      {statusQ.isLoading && (
        <p className={styles.cardLead}>Checking your payout setup…</p>
      )}

      {!statusQ.isLoading && statusQ.error && (
        <div className={styles.alertErr}>
          {statusQ.error instanceof Error
            ? statusQ.error.message
            : 'Failed to load payout status'}
        </div>
      )}

      {errorMsg && <div className={styles.alertErr}>{errorMsg}</div>}

      {!statusQ.isLoading && !statusQ.error && statusQ.data && (
        <PayoutsBody
          status={statusQ.data}
          busy={busy}
          onStart={handleStart}
          onOpenDashboard={handleOpenDashboard}
        />
      )}
    </section>
  );
}

interface PayoutsStatus {
  has_account: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements_currently_due: string[];
  requirements_disabled_reason: string | null;
  platform_held?: boolean;
}

function PayoutsBody({
  status, busy, onStart, onOpenDashboard,
}: {
  status: PayoutsStatus;
  busy: 'starting' | 'opening' | null;
  onStart: () => void;
  onOpenDashboard: () => void;
}) {
  const view = tenantPayoutsView(status);

  // Ruling 2: the Api's platform_held flag alone. No Start, Continue or
  // dashboard button; the Api refuses /connect/start for this tenant.
  if (view === 'platform_held') {
    return <p className={styles.cardLead}>{PLATFORM_HELD_PAYOUTS_COPY}</p>;
  }

  if (view === 'ready') {
    return (
      <>
        <p className={styles.cardLead}>
          <CheckCircle2
            size={14}
            style={{ display: 'inline', verticalAlign: '-2px', marginRight: 6, color: 'var(--color-accent)' }}
            aria-hidden
          />
          Payouts active. Stripe will deposit cleared session revenue
          to your connected bank account.
        </p>
        <div className={styles.payoutsActions}>
          <button
            type="button"
            className={styles.payoutsBtn}
            disabled={busy === 'opening'}
            onClick={onOpenDashboard}
          >
            {busy === 'opening' ? (
              <><Loader2 size={14} className={styles.spin} /> Opening Stripe…</>
            ) : (
              <><ExternalLink size={14} /> Open Stripe dashboard</>
            )}
          </button>
        </div>
      </>
    );
  }

  if (view === 'not_started') {
    return (
      <>
        <p className={styles.cardLead}>
          Connect your bank account to receive payouts from EVI. We
          use Stripe Express, so you sign up directly with Stripe and
          we hand off payment routing through your account.
        </p>
        <div className={styles.payoutsActions}>
          <button
            type="button"
            className={styles.payoutsBtn}
            disabled={busy === 'starting'}
            onClick={onStart}
          >
            {busy === 'starting' ? (
              <><Loader2 size={14} className={styles.spin} /> Opening Stripe…</>
            ) : (
              <><ExternalLink size={14} /> Start setup</>
            )}
          </button>
        </div>
      </>
    );
  }

  // has_account && !ready → onboarding in progress, missing items.
  const dueCount = status.requirements_currently_due.length;
  return (
    <>
      <p className={styles.cardLead}>
        <AlertCircle
          size={14}
          style={{ display: 'inline', verticalAlign: '-2px', marginRight: 6, color: 'var(--color-warn)' }}
          aria-hidden
        />
        {dueCount > 0
          ? `Stripe still needs ${dueCount} item${dueCount === 1 ? '' : 's'} from you before payouts can start.`
          : 'Your account is being reviewed. This usually takes a minute or two.'}
      </p>

      {status.requirements_disabled_reason && (
        <p className={styles.payoutsDisabledReason}>
          {status.requirements_disabled_reason}
        </p>
      )}

      {dueCount > 0 && (
        <ul className={styles.payoutsReqList}>
          {status.requirements_currently_due.slice(0, 6).map((req) => (
            <li key={req} className={styles.payoutsReqItem}>{humanizeReq(req)}</li>
          ))}
          {dueCount > 6 && (
            <li className={styles.payoutsReqMore}>+{dueCount - 6} more</li>
          )}
        </ul>
      )}

      <div className={styles.payoutsActions}>
        <button
          type="button"
          className={styles.payoutsBtn}
          disabled={busy === 'starting'}
          onClick={onStart}
        >
          {busy === 'starting' ? (
            <><Loader2 size={14} className={styles.spin} /> Opening Stripe…</>
          ) : (
            <><ExternalLink size={14} /> Continue setup</>
          )}
        </button>
      </div>
    </>
  );
}

/** Stripe requirement field names like `external_account` /
 *  `individual.dob.day` aren't friendly. Surface the cleaner section
 *  before the first dot, capitalized. Stripe's hosted onboarding
 *  shows the real form, so this label is a "still due" hint, not
 *  the source of truth. Mirrors humanizeReq on the resident
 *  ConnectCompletePage. */
function humanizeReq(req: string): string {
  const head = req.split('.')[0] ?? req;
  const spaced = head.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Platform Admin Fee section (Phase 7c of the agent-commission ticket).
 *
 * super_admin-only — gated at the parent (SettingsPage). The backend
 * PATCH /admin/tenants/:id/settings ALSO silently strips
 * admin_fee_bps from non-super_admin request bodies (mirror of Phase
 * 7b's Charger.agent_id pattern), so a tenant_admin who hand-crafts
 * a PATCH can't write the field. Frontend hiding + backend stripping
 * compose for defense-in-depth.
 *
 * UX:
 *   - Number input as percent (0–50%), step 0.1.
 *   - Empty string = null = revert to FALLBACK resolution.
 *   - Save button disabled until the value differs from the current
 *     server value (no spurious writes).
 *   - "Last changed <date> by user …<suffix>" line surfaces the
 *     denormalized audit metadata (Phase 1a fields). v1 renders the
 *     last 8 chars of the actor ObjectId; v2 will resolve to user
 *     name when a User-by-id helper ships.
 *
 * Composition with Phase 2's resolveAdminFeeBps:
 *   Precedence is Location.revenue_split > Tenant.admin_fee_bps >
 *   FALLBACK (15%). For any tenant whose Locations already have an
 *   explicit revenue_split (default 15 since Tier 2), setting this
 *   field has NO effect on those sessions — Location wins. The section
 *   copy below spells this out so the operator understands why a
 *   change here may not move fees on existing sessions.
 *
 * The floor (Tier 2): whatever resolves, a NEW session's pin is
 *   max(resolved, PLATFORM_FEE_FLOOR_BPS = 1500), so a value saved
 *   here below 15% settles at 15% on new sessions. The input keeps
 *   min 0 (the server's minimum); the copy states the floor.
 */
function PlatformAdminFeeSection({ tenantId }: { tenantId: string }) {
  const settingsQ = useQuery({
    queryKey: ['tenant-settings', tenantId],
    queryFn: () => fetchTenantSettings(tenantId),
    staleTime: 60_000,
  });

  // Edited as percent string, persisted as bps. Empty string = null
  // on save (revert to FALLBACK). Same boundary-conversion pattern
  // as Phase 7b's commissionBpsForSave.
  const initialPctStr = settingsQ.data
    ? (typeof settingsQ.data.admin_fee_bps === 'number'
      ? (settingsQ.data.admin_fee_bps / 100).toString()
      : '')
    : '';
  const [feePctStr, setFeePctStr] = useState<string | null>(null);
  // Settle to the server value once the query loads. `null` = "not
  // yet user-edited"; subsequent user edits drop the null state.
  const effectivePctStr = feePctStr ?? initialPctStr;

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // bps for the wire. Empty string → null (revert). NaN / negative →
  // null (treat malformed as a clear). Caps at 50000 bps at the form
  // layer; the JSON-schema body validator + Mongoose validators both
  // enforce the same bound server-side.
  const feeBpsForSave = (() => {
    const trimmed = effectivePctStr.trim();
    if (!trimmed) return null;
    const pct = Number(trimmed);
    if (!Number.isFinite(pct) || pct < 0) return null;
    return Math.min(50_000, Math.round(pct * 100));
  })();

  // Dirty check — disable save when the user hasn't actually changed
  // the value (defends against double-clicks + skips the audit-stamp
  // server-side anyway since the PATCH compares previous vs current).
  const serverBps = settingsQ.data?.admin_fee_bps ?? null;
  const dirty = feeBpsForSave !== serverBps;

  const handleSave = async (): Promise<void> => {
    setSaveErr('');
    setSaving(true);
    try {
      await updateTenantSettings(tenantId, { admin_fee_bps: feeBpsForSave });
      await settingsQ.refetch();
      setFeePctStr(null); // reset to "server is source of truth"
      setSavedAt(Date.now());
    } catch (err) {
      setSaveErr(await getDisplayErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const changedAt = settingsQ.data?.admin_fee_changed_at ?? null;
  const changedBy = settingsQ.data?.admin_fee_changed_by ?? null;

  return (
    <section className={styles.platformFeeSection}>
      <div className={styles.platformFeeHeader}>
        <div className={styles.headerIcon}><DollarSign size={16} /></div>
        <div>
          <h2 className={styles.platformFeeTitle}>Platform admin fee</h2>
          <p className={styles.platformFeeHint}>
            EVI&rsquo;s cut of session revenue at this tenant. Used as a
            fallback when a Location doesn&rsquo;t have its own
            <code> revenue_split</code> set. Default is 15%; values
            below 15% are floored at settlement for new sessions. A
            Location with its own <code>revenue_split</code> (new ones
            are stamped 15) sees no change from this field —{' '}
            <strong>Location wins precedence</strong>. Audit-stamped on
            every change.
          </p>
        </div>
      </div>

      {settingsQ.isLoading ? (
        <p className={styles.platformFeeHint}>
          <Loader2 size={13} className={styles.spin} /> Loading…
        </p>
      ) : settingsQ.isError ? (
        <p className={styles.platformFeeHint} style={{ color: 'var(--color-err)' }}>
          Couldn&rsquo;t load tenant settings. Refresh to retry.
        </p>
      ) : (
        <>
          <div className={styles.platformFeeField}>
            <label className={styles.platformFeeLabel}>
              Platform fee (%)
            </label>
            <div className={styles.platformFeeInputRow}>
              <input
                className={styles.platformFeeInput}
                type="number"
                min={0}
                max={50}
                step={0.1}
                placeholder="e.g. 15 (default)"
                value={effectivePctStr}
                onChange={(e) => setFeePctStr(e.target.value)}
                disabled={saving}
              />
              <button
                type="button"
                className={styles.platformFeeBtn}
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                {saving ? (
                  <><Loader2 size={13} className={styles.spin} /> Saving…</>
                ) : savedAt && !dirty ? (
                  <><Check size={13} /> Saved</>
                ) : (
                  <>Save</>
                )}
              </button>
            </div>
            <p className={styles.platformFeeHint}>
              0–50%. Stored as basis points. Empty = use FALLBACK
              (15%). Values below 15% are floored at settlement for new
              sessions. Saving with the same value is a no-op — audit
              metadata stamps only on actual changes.
            </p>
          </div>

          {saveErr && (
            <p className={styles.platformFeeHint} style={{ color: 'var(--color-err)' }}>
              {saveErr}
            </p>
          )}

          {/* Audit metadata read-only. Denormalized from AuditLog
              via the Tenant doc's admin_fee_changed_at +
              admin_fee_changed_by fields (Phase 1a schema). User
              suffix is the last 8 of the ObjectId — sufficient for
              forensics without an extra User-by-id lookup; v2 will
              resolve to a name when that helper ships. */}
          {changedAt && (
            <p className={styles.platformFeeAudit}>
              Last changed {new Date(changedAt).toLocaleString()}
              {changedBy && (
                <> by user &hellip;{changedBy.slice(-8)}</>
              )}
            </p>
          )}
        </>
      )}
    </section>
  );
}
